/** @jsxImportSource @emotion/react */
"use client";

import type { MediaSizes } from "@packages/media";
import { useCurrentQueueItem, usePlayer } from "@packages/media";
import * as Toolbar from "@radix-ui/react-toolbar";
import type { MouseEventHandler } from "react";
import { useMemo } from "react";

import {
  coreMediaStyles,
  playerButtonStyles,
  playerControlsContainerStyles,
  useMediaClass,
} from "../../../../utils/media.emotion";
import { Scrubber } from "../../controls/Scrubber/Scrubber.emotion";
import { PlaybackControls } from "../PlaybackControls/PlaybackControls.emotion";

export type MediaControlsProps = {
  size: MediaSizes;
  onClose: MouseEventHandler<HTMLButtonElement>;
  onMouseMove?: MouseEventHandler;
  showControls?: boolean;
};

export const MediaControls = ({
  size,
  onClose,
  onMouseMove,
  showControls,
}: MediaControlsProps) => {
  const player = usePlayer();
  const { currentItem } = useCurrentQueueItem();
  const mediaClass = useMediaClass();

  const controlsContainerStyle = useMemo(
    () => playerControlsContainerStyles[mediaClass],
    [mediaClass],
  );
  const buttonStyle = useMemo(
    () => playerButtonStyles[mediaClass],
    [mediaClass],
  );

  const preventExpanding: MouseEventHandler<HTMLDivElement> = (e) =>
    e.stopPropagation();

  const scrubberAboveControls =
    mediaClass.includes("expanded") &&
    (!mediaClass.includes("video") ||
      (mediaClass.includes("mobile") && mediaClass.includes("portrait")));
  const scrubberBelowControls =
    mediaClass.includes("collapsed-desktop") ||
    ((mediaClass.includes("expanded") || mediaClass.includes("fullscreen")) &&
      mediaClass.includes("video") &&
      mediaClass.includes("desktop"));

  return (
    <div
      key={"mediaPlayerControls"}
      css={[
        controlsContainerStyle,
        coreMediaStyles.centerBase,
        showControls ? coreMediaStyles.centerVisible : null,
      ]}
      onClick={preventExpanding}
      onMouseMove={onMouseMove}
    >
      {scrubberAboveControls ? (
        <Scrubber
          disabled={currentItem?.type === "radio_song"}
          durationS={player?.duration ?? 0}
          elapsedS={player?.currentTime ?? 0}
          jumpTo={(t) => player?.setTime(t)}
          showTime={"under"}
          css={coreMediaStyles.scrubber}
        />
      ) : null}

      <div key={"playerButtonContainer"} css={buttonStyle}>
        <PlaybackControls
          Wrapper={Toolbar.Button}
          size={size}
          onClose={onClose}
          showControls={showControls}
        />
      </div>

      {scrubberBelowControls ? (
        <Scrubber
          disabled={currentItem?.type === "radio_song"}
          durationS={player?.duration ?? 0}
          elapsedS={player?.currentTime ?? 0}
          jumpTo={(t) => player?.setTime(t)}
          css={[
            mediaClass.includes("fullscreen") ||
            (mediaClass.includes("expanded") && mediaClass.includes("video"))
              ? [coreMediaStyles.fullscreenScrubber, coreMediaStyles.clickable]
              : null,
            coreMediaStyles.edgeBase,
            coreMediaStyles.bottomEdge,
            showControls ? coreMediaStyles.edgeVisible : null,
          ]}
          showTime={
            mediaClass.includes("collapsed-desktop")
              ? "beside"
              : mediaClass.includes("expanded") ||
                  mediaClass.includes("fullscreen-desktop")
                ? "under"
                : false
          }
        />
      ) : null}
    </div>
  );
};
